"use client"
import type React from "react"
import Modal from "Components/UI/Modal/"
import { type FC, useState, useEffect, useRef, useCallback, useMemo, Fragment, useLayoutEffect } from "react"
import { Chat } from "./Chat"
import { Icon } from "Components/UI"
import "./AiChatModal.override.scss"
import bombeirosIcon from "Static/images/bombeirosicon.jpeg"
import { useGetThreads } from "./Chat/hooks"
import { Spin, Tooltip } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import AssistantLibrary from "./components/AssistantLibrary"
import planoDiretorGoianiaIcon from "Static/images/plano-diretor-goiania.png"

export interface AiChatProps {
  constructionSiteId: number
  showAiModal: boolean
  toggleAiModal: () => void
}

export const AiChatModal: FC<AiChatProps> = ({ constructionSiteId, showAiModal, toggleAiModal }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(true)
  const [isExpanded, setIsExpanded] = useState(false)
  const [search, setSearch] = useState<string>("")
  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>(undefined)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [currentView, setCurrentView] = useState<'chat' | 'assistant-library'>('chat')
  const [selectedAssistant, setSelectedAssistant] = useState<string | undefined>(undefined)
  const [pinnedSide, setPinnedSide] = useState<'left' | 'right'>('right')
  const [isPinned, setIsPinned] = useState(true)

  const getInitialPosition = () => {
    const defaultWidth = 1000
    return {
      x: window.innerWidth - defaultWidth - 24,
      y: 23
    }
  }

  const [position, setPosition] = useState(getInitialPosition())
  const [isDragging, setIsDragging] = useState(false)
  const [leftDropZoneVisible, setLeftDropZoneVisible] = useState(false)
  const [rightDropZoneVisible, setRightDropZoneVisible] = useState(false)
  const [activeDropZone, setActiveDropZone] = useState<'left' | 'right' | null>(null)
  const positionRef = useRef(getInitialPosition())
  const bodyScrollStateRef = useRef<{
    overflow: string
    paddingRight: string
    hadModalOpenClass: boolean
  } | null>(null)

  const dragData = useRef<{
    startX: number
    startY: number
    dragging: boolean
    pointerId: number | null
    width: number
    height: number
  }>({
    startX: 0,
    startY: 0,
    dragging: false,
    pointerId: null,
    width: 0,
    height: 0,
  })

  const pendingDeltaRef = useRef({ x: 0, y: 0 })
  const dragFrameRef = useRef<number | null>(null)

  const modalRef = useRef<HTMLElement | null>(null)
  const lastPinnedSideRef = useRef<'left' | 'right'>(pinnedSide)

  const getModalContainer = useCallback(() => document.body, [])

  const getModalWidth = () => {
    if (isExpanded) return window.innerWidth - 40
    return isPanelOpen ? 1000 : 600
  }

  const modalWidth = getModalWidth()
  const DROP_ZONE_OUTSIDE_THRESHOLD = 64

  const setImportantStyle = useCallback((el: HTMLElement, prop: string, value: string) => {
    el.style.setProperty(prop, value, 'important')
  }, [])

  const clampPosition = useCallback((x: number, y: number, width: number, height?: number) => {
    const modalHeight = height ?? modalRef.current?.offsetHeight ?? window.innerHeight * 0.6

    const horizontalOverflow = Math.min(width * 0.85, 420)
    const verticalOverflow = Math.min(modalHeight * 0.85, 360)

    const minX = -horizontalOverflow
    const maxX = Math.max(minX, window.innerWidth - width + horizontalOverflow)
    const minY = 0
    const maxY = Math.max(minY, window.innerHeight - modalHeight + verticalOverflow)

    const clampedX = Math.min(Math.max(minX, x), maxX)
    const clampedY = Math.min(Math.max(minY, y), maxY)

    return { x: clampedX, y: clampedY }
  }, [])

  const updatePositionWithDelta = useCallback(
    (deltaX: number, deltaY: number, dragWidth: number, dragHeight?: number) => {
      const prev = positionRef.current
      const candidateX = prev.x + deltaX
      const candidateY = prev.y + deltaY
      const nextPosition = clampPosition(candidateX, candidateY, dragWidth, dragHeight)

      if (prev.x === nextPosition.x && prev.y === nextPosition.y) {
        return null
      }

      positionRef.current = nextPosition

      if (modalRef.current) {
        setImportantStyle(modalRef.current, 'left', `${nextPosition.x}px`)
        setImportantStyle(modalRef.current, 'top', `${nextPosition.y}px`)
        setImportantStyle(modalRef.current, 'right', 'auto')
        setImportantStyle(modalRef.current, 'bottom', 'auto')
        setImportantStyle(modalRef.current, 'transform', 'none')
      }

      return nextPosition
    },
    [clampPosition, setImportantStyle],
  )

  const updateDropZones = useCallback(
    (positionValue: { x: number; y: number }, width: number) => {
      if (!dragData.current.dragging) return

      const viewportWidth = window.innerWidth
      const leftOvershoot = Math.abs(Math.min(0, positionValue.x))
      const rightOvershoot = Math.max(0, positionValue.x + width - viewportWidth)

      const leftZoneActive = leftOvershoot >= DROP_ZONE_OUTSIDE_THRESHOLD
      const rightZoneActive = rightOvershoot >= DROP_ZONE_OUTSIDE_THRESHOLD

      setLeftDropZoneVisible((prev) => (prev === leftZoneActive ? prev : leftZoneActive))
      setRightDropZoneVisible((prev) => (prev === rightZoneActive ? prev : rightZoneActive))

      const nextActiveZone = leftZoneActive && !rightZoneActive
        ? 'left'
        : rightZoneActive && !leftZoneActive
          ? 'right'
          : null

      setActiveDropZone((prev) => (prev === nextActiveZone ? prev : nextActiveZone))
    },
    [DROP_ZONE_OUTSIDE_THRESHOLD],
  )

  const applyPendingMovement = useCallback(() => {
    dragFrameRef.current = null

    const pending = pendingDeltaRef.current
    if (!pending.x && !pending.y) {
      return
    }

    pendingDeltaRef.current = { x: 0, y: 0 }

    const dragWidth = dragData.current.width || modalWidth
    const dragHeight = dragData.current.height || window.innerHeight * 0.6
    const nextPosition = updatePositionWithDelta(pending.x, pending.y, dragWidth, dragHeight)

    if (nextPosition) {
      updateDropZones(nextPosition, dragWidth)
    }
  }, [modalWidth, updateDropZones, updatePositionWithDelta])

  const schedulePendingMovement = useCallback(() => {
    if (dragFrameRef.current !== null) return
    dragFrameRef.current = requestAnimationFrame(applyPendingMovement)
  }, [applyPendingMovement])

  const flushPendingMovement = useCallback(() => {
    if (dragFrameRef.current !== null) {
      cancelAnimationFrame(dragFrameRef.current)
      dragFrameRef.current = null
    }

    const pending = pendingDeltaRef.current
    pendingDeltaRef.current = { x: 0, y: 0 }

    if (!pending.x && !pending.y) {
      return positionRef.current
    }

    const dragWidth = dragData.current.width || modalWidth
    const dragHeight = dragData.current.height || window.innerHeight * 0.6
    const nextPosition = updatePositionWithDelta(pending.x, pending.y, dragWidth, dragHeight)

    return nextPosition ?? positionRef.current
  }, [modalWidth, updatePositionWithDelta])



  useEffect(() => {
    positionRef.current = position
    if (modalRef.current) {
      modalRef.current.style.left = `${position.x}px`
      modalRef.current.style.top = `${position.y}px`
      setImportantStyle(modalRef.current, 'transform', 'none')
    }
  }, [position, setImportantStyle])

  useEffect(
    () => () => {
      if (dragFrameRef.current !== null) {
        cancelAnimationFrame(dragFrameRef.current)
      }
    },
    [],
  )

  const { data: threads, isLoading: isLoadingThreads } = useGetThreads(constructionSiteId)

  const assistants = [
    { id: 1, name: "Biblioteca de Assistentes", iconName: "assistentes", type: "icon" },
    { id: 2, name: "CBM-GO, Corpo de Bombeiros...", imagePath: bombeirosIcon, type: "image" },
    { id: 3, name: "Prefeitura de Goiânia", imagePath: planoDiretorGoianiaIcon, type: "image" },
    { id: 4, name: "ABNT NBR 15575 - Norma de...", iconName: "abnt", type: "icon" },
  ]

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || isExpanded) return

      e.preventDefault()
      e.stopPropagation()

      setIsPinned(false)

      const rect = modalRef.current?.getBoundingClientRect()
      if (rect) {
        positionRef.current = { x: rect.left, y: rect.top }
      }

      dragData.current.dragging = true
      dragData.current.pointerId = e.pointerId
      dragData.current.startX = e.clientX
      dragData.current.startY = e.clientY
      dragData.current.width = rect?.width ?? modalWidth
      dragData.current.height = rect?.height ?? modalRef.current?.offsetHeight ?? window.innerHeight * 0.6
      pendingDeltaRef.current = { x: 0, y: 0 }
      if (dragFrameRef.current !== null) {
        cancelAnimationFrame(dragFrameRef.current)
        dragFrameRef.current = null
      }

      document.body.style.userSelect = "none"
      if (modalRef.current) {
        modalRef.current.style.cursor = "grabbing"
        modalRef.current.style.transition = "none"
        setImportantStyle(modalRef.current, 'top', `${positionRef.current.y}px`)
        setImportantStyle(modalRef.current, 'left', `${positionRef.current.x}px`)
        setImportantStyle(modalRef.current, 'right', 'auto')
        setImportantStyle(modalRef.current, 'bottom', 'auto')
      }

      setIsDragging(true)
      setActiveDropZone(null)
      setLeftDropZoneVisible(false)
      setRightDropZoneVisible(false)
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [isExpanded, modalWidth, setImportantStyle],
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragData.current.dragging || dragData.current.pointerId !== e.pointerId) return

      const deltaX = e.clientX - dragData.current.startX
      const deltaY = e.clientY - dragData.current.startY

      dragData.current.startX = e.clientX
      dragData.current.startY = e.clientY

      pendingDeltaRef.current.x += deltaX
      pendingDeltaRef.current.y += deltaY

      schedulePendingMovement()
    },
    [schedulePendingMovement],
  )

  const endPointerDrag = useCallback((target: HTMLDivElement, pointerId: number) => {
    dragData.current.dragging = false
    dragData.current.pointerId = null
    document.body.style.userSelect = ""
    if (modalRef.current) {
      modalRef.current.style.cursor = ""
      modalRef.current.style.transition = "all 0.3s ease"
    }

    if (target.hasPointerCapture(pointerId)) {
      target.releasePointerCapture(pointerId)
    }
  }, [])

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragData.current.dragging || dragData.current.pointerId !== e.pointerId) return

      flushPendingMovement()
      const dropZone = activeDropZone
      endPointerDrag(e.currentTarget, e.pointerId)
      setIsDragging(false)
      setActiveDropZone(null)
      setLeftDropZoneVisible(false)
      setRightDropZoneVisible(false)

      if (dropZone) {
        setIsPinned(true)
        setPinnedSide(dropZone)
        const rect = modalRef.current?.getBoundingClientRect()
        const width = rect?.width ?? modalWidth
        const targetX = dropZone === 'right'
          ? window.innerWidth - width - 24
          : 24
        const targetY = 23
        setPosition({ x: targetX, y: targetY })
      } else {
        setIsPinned(false)
        setPosition(prev => {
          const next = positionRef.current
          if (prev.x === next.x && prev.y === next.y) {
            return prev
          }
          return { ...next }
        })
      }
    },
    [endPointerDrag, activeDropZone, flushPendingMovement],
  )

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragData.current.dragging || dragData.current.pointerId !== e.pointerId) return

      flushPendingMovement()
      endPointerDrag(e.currentTarget, e.pointerId)
      setIsDragging(false)
      setActiveDropZone(null)
      setLeftDropZoneVisible(false)
      setRightDropZoneVisible(false)
      setPosition(prev => {
        const next = positionRef.current
        if (prev.x === next.x && prev.y === next.y) {
          return prev
        }
        return { ...next }
      })
    },
    [endPointerDrag, flushPendingMovement],
  )

  useLayoutEffect(() => {
    const modal = document.querySelector(".ai-chat-modal .ant-modal")
    if (modal) {
      const modalEl = modal as HTMLElement
      modalRef.current = modalEl
      modalEl.style.width = `${modalWidth}px`
      modalEl.style.maxWidth = "none"
      modalEl.style.transition = dragData.current.dragging ? "none" : "all 0.3s ease"
      modalEl.style.willChange = "transform"

      if (isExpanded) {
        modalEl.style.top = "20px"
        modalEl.style.right = "20px"
        modalEl.style.bottom = "20px"
        modalEl.style.left = "20px"
        modalEl.style.height = "calc(100vh - 40px)"
        setImportantStyle(modalEl, 'transform', 'none')
      } else {
        modalEl.style.transition = "none"
        modalEl.style.position = "fixed"

        // Quando não está fixado, aplicar posição com !important para sobrescrever CSS
        if (!isPinned) {
          setImportantStyle(modalEl, 'left', `${positionRef.current.x}px`)
          setImportantStyle(modalEl, 'top', `${positionRef.current.y}px`)
          setImportantStyle(modalEl, 'right', 'auto')
          setImportantStyle(modalEl, 'bottom', 'auto')
        } else {
          // Quando fixado, remover !important e deixar CSS tomar controle
          modalEl.style.left = `${positionRef.current.x}px`
          modalEl.style.top = `${positionRef.current.y}px`
          modalEl.style.right = "auto"
          modalEl.style.bottom = "auto"
        }

        modalEl.style.height = "auto"
        setImportantStyle(modalEl, 'transform', 'none')
        modalEl.style.margin = "0"

        requestAnimationFrame(() => {
          if (!modalRef.current) return
          modalRef.current.style.transition = dragData.current.dragging ? "none" : "all 0.3s ease"
          setImportantStyle(modalRef.current, 'transform', 'none')
        })
      }

      const wrapper = modal.closest(".ant-modal-wrap")
      if (wrapper) {
        ; (wrapper as HTMLElement).style.overflow = "visible"
      }
    }
  }, [isPanelOpen, modalWidth, isExpanded, pinnedSide, position, isPinned, setImportantStyle])

  useEffect(() => {

    const handleResize = () => {
      const height = modalRef.current?.offsetHeight || window.innerHeight * 0.6
      setPosition(prev => {
        const nextPosition = clampPosition(prev.x, prev.y, modalWidth, height)
        if (prev.x === nextPosition.x && prev.y === nextPosition.y) {
          return prev
        }

        return nextPosition
      })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [clampPosition, modalWidth])

  useEffect(() => {
    if (!showAiModal) {
      if (bodyScrollStateRef.current) {
        const saved = bodyScrollStateRef.current
        const bodyEl = document.body
        bodyEl.style.overflow = saved.overflow
        bodyEl.style.paddingRight = saved.paddingRight
        if (saved.hadModalOpenClass) {
          bodyEl.classList.add("ant-modal-open")
        } else {
          bodyEl.classList.remove("ant-modal-open")
        }
        bodyScrollStateRef.current = null
      }
      return
    }

    const bodyEl = document.body
    bodyScrollStateRef.current = {
      overflow: bodyEl.style.overflow,
      paddingRight: bodyEl.style.paddingRight,
      hadModalOpenClass: bodyEl.classList.contains("ant-modal-open"),
    }

    bodyEl.classList.remove("ant-modal-open")
    bodyEl.style.overflow = ""
    bodyEl.style.paddingRight = ""

    return () => {
      const saved = bodyScrollStateRef.current
      if (!saved) return

      const currentBody = document.body
      currentBody.style.overflow = saved.overflow
      currentBody.style.paddingRight = saved.paddingRight
      if (saved.hadModalOpenClass) {
        currentBody.classList.add("ant-modal-open")
      } else {
        currentBody.classList.remove("ant-modal-open")
      }
      bodyScrollStateRef.current = null
    }
  }, [showAiModal])

  useEffect(() => {

    const height = modalRef.current?.offsetHeight || window.innerHeight * 0.6
    setPosition(prev => {
      const nextPosition = clampPosition(prev.x, prev.y, modalWidth, height)
      if (prev.x === nextPosition.x && prev.y === nextPosition.y) {
        return prev
      }

      return nextPosition
    })
  }, [modalWidth, clampPosition])

  const togglePanel = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsPanelOpen((prev) => {
      return !prev
    })
  }

  const toggleSearchInput = () => {
    setIsSearchExpanded(prev => !prev)
    if (isSearchExpanded) {
      setSearch("")
    }
  }

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded(prev => !prev)
  }

  const groupedThreads = useMemo(() => {
    if (!threads) return []

    const now = new Date()

    const normalize = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

    const getLabel = (createdAt: Date) => {
      const diffMs = normalize(now).getTime() - normalize(createdAt).getTime()
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return "Hoje"
      if (diffDays === 1) return "Ontem"
      if (diffDays <= 7) return "Últimos 7 dias"
      return "Últimos 30 dias"
    }

    const map = new Map<string, typeof threads>()

    threads
      .filter((t) => t.resume.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .forEach((thread) => {
        const label = getLabel(new Date(thread.createdAt))
        if (!map.has(label)) map.set(label, [])
        map.get(label)!.push(thread)
      })

    return Array.from(map.entries())
  }, [threads, search])

  const shouldShowEditThreadIcon =
    !isPanelOpen &&
    !isExpanded &&
    currentView === 'chat' &&
    Boolean(selectedThreadId)

  return (
    <>
      <Modal
        wrapClassName={`ai-chat-modal ${isExpanded ? "expanded" : ""} ${isPinned ? `pinned pinned-${pinnedSide}` : "unpinned"}`}
        visible={showAiModal}
        onCancel={toggleAiModal}
        mask={false}
        maskClosable={false}
        zIndex={999}
        getContainer={getModalContainer}
        footerButtons={false}
        title={null}
        customClassName={true}
        width={modalWidth}
        style={{
          width: modalWidth,
          position: "fixed",
          margin: 0,
        }}
      >
        <div
          className="custom-header"
        >
          <div className={`custom-header-left ${isPanelOpen ? "panel-expanded" : ""}`}>
            {isPanelOpen && (
              <div className="assistente-badge">
                <span>Coordia - Assistente de IA</span>
              </div>
            )}
            <div className="panel-toggle" onClick={(e) => {
              e.stopPropagation()
              togglePanel(e)
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Tooltip
                  title={isPanelOpen ? "Fechar barra lateral" : "Abrir barra lateral"}
                  placement="bottom"
                  mouseEnterDelay={0.3}
                >
                  <span style={{ display: "flex" }}>
                    <Icon icon="panels2" customSize={20} customColor="#8697A8" />
                  </span>
                </Tooltip>

                {shouldShowEditThreadIcon && (
                  <span
                    className="edit-thread-button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedThreadId(undefined)
                      setSelectedAssistant(undefined)
                      setCurrentView('chat')
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Novo chat"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        setSelectedThreadId(undefined)
                        setSelectedAssistant(undefined)
                        setCurrentView('chat')
                      }
                    }}
                  >
                    <Icon icon="editar" customSize={16} customColor="#8697A8" />
                  </span>
                )}

                <span className="custom-title">Novo chat</span>
              </div>
            </div>
          </div>
          <div
            className="header-drag-area"
            onPointerDown={!isExpanded ? handlePointerDown : undefined}
            onPointerMove={!isExpanded ? handlePointerMove : undefined}
            onPointerUp={!isExpanded ? handlePointerUp : undefined}
            onPointerCancel={!isExpanded ? handlePointerCancel : undefined}
            style={{ cursor: !isExpanded ? "grab" : "default" }}
          />
          <div className="custom-icons">
            <span className="icon-wrapper" onClick={(e) => {
              e.stopPropagation()
              toggleExpanded(e)
            }}>
              <Icon icon={isExpanded ? "lessexpand" : "arrowExpand"} customSize={25} customColor="#8697A8" />
            </span>
            <span className="icon-wrapper" onClick={(e) => {
              e.stopPropagation()
              toggleAiModal()
            }}>
              <Icon icon="cancelar" customSize={12} customColor="#8697A8" />
            </span>
          </div>
        </div>

        <div className="chat-container">
          <div className={`side-panel ${isPanelOpen ? "open" : ""}`}>
            <div className="panel-header">
              {!isSearchExpanded ? (
                <>
                  <button className="new-chat-button" onClick={() => {
                    setSelectedThreadId(undefined)
                    setSelectedAssistant(undefined)
                    setCurrentView('chat')
                  }}>
                    <div className="button-content">
                      <Icon icon="editar" customSize={16} customColor="#8697A8" />
                      <span>Novo Chat</span>
                    </div>
                  </button>
                  <button className="search-icon-button" onClick={toggleSearchInput}>
                    <Icon icon="buscar" customSize={16} customColor="#8697A8" />
                  </button>
                </>
              ) : (
                <div className="search-expanded-container">
                  <div className="search-input-container">
                    <Icon icon="buscar" customSize={16} customColor="#8697A8" />
                    <input
                      type="text"
                      placeholder="Pesquisar por chat"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="search-input"
                      autoFocus
                    />
                    <button className="search-close-button" onClick={toggleSearchInput}>
                      <Icon icon="cancelar" customSize={12} customColor="#8697A8" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="content-divider" />

            <div className="panel-content">
              <div className="chat-history">
                <div className="chat-list">
                  <div className="assistants-section">
                    <div className="section-label">Assistentes</div>
                    {assistants.map((assistant) => {
                      return (
                        <div
                          key={assistant.id}
                          className="assistant-item"
                          onClick={() => {
                            if (assistant.name === "Biblioteca de Assistentes") {
                              setCurrentView(currentView === 'assistant-library' ? 'chat' : 'assistant-library')
                            } else if (selectedThreadId !== undefined) {
                            } else if (assistant.id === 2) {
                              setSelectedAssistant('Bombeiros')
                              setCurrentView('chat')
                            } else if (assistant.id === 3) {
                              setSelectedAssistant('PrefeituraGoiania')
                              setCurrentView('chat')
                            } else {
                              setSelectedAssistant(undefined)
                              setCurrentView('chat')
                            }
                          }}
                        >
                          <div className="assistant-icon" data-assistant={assistant.type === "icon" ? assistant.iconName : "image"}>
                            {assistant.type === "icon" ? (
                              <Icon icon={assistant.iconName as any} customSize={20} />
                            ) : (
                              <img
                                src={assistant.imagePath}
                                alt={assistant.name}
                                style={{ width: 20, height: 20, objectFit: 'contain' }}
                              />
                            )}
                          </div>
                          <span>{assistant.name}</span>
                        </div>
                      )
                    })}
                  </div>

                  {isLoadingThreads ? (
                    <div className="loading-item">
                      <Spin indicator={<LoadingOutlined style={{ fontSize: 32, marginRight: 6 }} />} />
                    </div>
                  ) : groupedThreads.length > 0 ? (
                    groupedThreads.map(([section, list]) => (
                      <Fragment key={section}>
                        <div className="section-label">{section}</div>
                        {list.map((thread) => (
                          <div
                            key={thread.threadId}
                            className="chat-item"
                            onClick={() => setSelectedThreadId(thread.threadId)}
                          >
                            <span>{thread.resume.split('\n\n[')[0].trim()}</span>
                          </div>
                        ))}
                      </Fragment>
                    ))
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className={`chat-content ${isPanelOpen ? "with-panel" : ""}`}>
            {currentView === 'chat' ? (
              <Chat
                constructionSiteId={constructionSiteId}
                selectedThreadId={selectedThreadId}
                setSelectedThreadId={setSelectedThreadId}
                assistant={selectedAssistant}
                onViewAll={() => setCurrentView('assistant-library')}
              />
            ) : (
              <AssistantLibrary
                onSelectAssistant={(assistant) => {
                  if (selectedThreadId !== undefined) {
                    setCurrentView('chat')
                    return
                  }
                  setSelectedAssistant(assistant)
                  setCurrentView('chat')
                }}
              />
            )}
          </div>
        </div>
      </Modal>
      {isDragging && leftDropZoneVisible && (
        <div
          className={`pin-drop-zone left show${activeDropZone === 'left' ? ' active' : ''}`}
          aria-hidden="true"
        />
      )}
      {isDragging && rightDropZoneVisible && (
        <div
          className={`pin-drop-zone right show${activeDropZone === 'right' ? ' active' : ''}`}
          aria-hidden="true"
        />
      )}
    </>
  )
}